import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
} from '@mui/material';
import {
  LightMode,
  DarkMode,
  Language as LanguageIcon,
  Check,
} from '@mui/icons-material';
import { useSettings } from '@/context/SettingsContext';

const SettingsSelector: React.FC = () => {
  const { language, themeMode, setLanguage, setThemeMode } = useSettings();
  const [themeAnchorEl, setThemeAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const themeOpen = Boolean(themeAnchorEl);
  const langOpen = Boolean(langAnchorEl);

  const handleThemeClick = (event: React.MouseEvent<HTMLElement>) => {
    setThemeAnchorEl(event.currentTarget);
  };

  const handleThemeClose = () => {
    setThemeAnchorEl(null);
  };

  const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = () => {
    setLangAnchorEl(null);
  };

  const handleThemeChange = (mode: 'light' | 'dark') => {
    setThemeMode(mode);
    handleThemeClose();
  };

  const handleLanguageChange = (lang: 'en' | 'ar') => {
    setLanguage(lang);
    handleLangClose();
  };

  const translations = {
    en: {
      theme: 'Theme',
      lightMode: 'Light Mode',
      darkMode: 'Dark Mode',
      language: 'Language',
      english: 'English',
      arabic: 'العربية',
    },
    ar: {
      theme: 'المظهر',
      lightMode: 'الوضع الفاتح',
      darkMode: 'الوضع الداكن',
      language: 'اللغة',
      english: 'English',
      arabic: 'العربية',
    },
  };

  const t = translations[language];

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {/* Language Selector */}
      <Tooltip title={t.language}>
        <IconButton
          onClick={handleLangClick}
          size="large"
          aria-label="language"
          aria-controls={langOpen ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={langOpen ? 'true' : undefined}
          color="inherit"
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={langAnchorEl}
        open={langOpen}
        onClose={handleLangClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: language === 'ar' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: language === 'ar' ? 'left' : 'right',
        }}
      >
        <MenuItem onClick={() => handleLanguageChange('en')}>
          <ListItemText>{t.english}</ListItemText>
          {language === 'en' && (
            <ListItemIcon>
              <Check fontSize="small" />
            </ListItemIcon>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleLanguageChange('ar')}>
          <ListItemText>{t.arabic}</ListItemText>
          {language === 'ar' && (
            <ListItemIcon>
              <Check fontSize="small" />
            </ListItemIcon>
          )}
        </MenuItem>
      </Menu>

      {/* Theme Selector */}
      <Tooltip title={t.theme}>
        <IconButton
          onClick={handleThemeClick}
          size="large"
          aria-label="theme"
          aria-controls={themeOpen ? 'theme-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={themeOpen ? 'true' : undefined}
          color="inherit"
        >
          {themeMode === 'light' ? <LightMode /> : <DarkMode />}
        </IconButton>
      </Tooltip>
      <Menu
        id="theme-menu"
        anchorEl={themeAnchorEl}
        open={themeOpen}
        onClose={handleThemeClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: language === 'ar' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: language === 'ar' ? 'left' : 'right',
        }}
      >
        <MenuItem onClick={() => handleThemeChange('light')}>
          <ListItemIcon>
            <LightMode fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t.lightMode}</ListItemText>
          {themeMode === 'light' && (
            <ListItemIcon>
              <Check fontSize="small" />
            </ListItemIcon>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('dark')}>
          <ListItemIcon>
            <DarkMode fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t.darkMode}</ListItemText>
          {themeMode === 'dark' && (
            <ListItemIcon>
              <Check fontSize="small" />
            </ListItemIcon>
          )}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SettingsSelector;
